import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { paginatedSchema } from "../pagination";

import { organisationSchema } from "./organisations";

export const layerCollectionListItemSchema = z.object({
  url: z.string(),
  slug: z.string(),
  organisation: organisationSchema,
  access_modifier: z.string(),
  supplier: z.string().nullable(),
  created: lizardDateSchema,
  last_modified: lizardDateSchema.nullable(),
});

export type LayerCollectionListItem = z.infer<typeof layerCollectionListItemSchema>;

export const layerCollectionListSchema = paginatedSchema(layerCollectionListItemSchema);
export type LayerCollectionList = z.infer<typeof layerCollectionListSchema>;

export const layerCollectionSchema = layerCollectionListItemSchema;
export type LayerCollection = z.infer<typeof layerCollectionSchema>;

export function useLayerCollections(params = {}) {
  const url = combineUrlAndParams("/api/v4/layercollections/", params);
  return useParsedQuery<LayerCollectionList>(
    ["lizard-api-v4", "layercollections", url],
    () => fetchWithError(url),
    (json: unknown) => layerCollectionListSchema.parse(json)
  );
}

export function useAllLayerCollections(params = {}) {
  const url = combineUrlAndParams("/api/v4/layercollections/?page_size=0", params);
  return useParsedQuery<LayerCollection[]>(
    ["lizard-api-v4", "layercollections", url],
    () => fetchWithError(url),
    (json: unknown) => z.array(layerCollectionSchema).parse(json)
  );
}

export function useLayerCollection(uuid: string) {
  return useParsedQuery<LayerCollection>(
    ["lizard-api-v4", "layercollections", uuid],
    () => fetchWithError(`/api/v4/layercollections/${uuid}/`),
    (json: unknown) => layerCollectionSchema.parse(json)
  );
}

export function useMultipleLayerCollections(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "layercollections", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/layercollections/${uuid}/`),
        parseFunction: (json: unknown) => layerCollectionSchema.parse(json),
      };
    })
  );
}
